// gpu.ts — the only place that talks to the WebGPU adapter directly.
// Everything above this file pretends the GPU is a friendly object.

export class GPUUnsupportedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "GPUUnsupportedError";
  }
}

export interface GPUSize {
  readonly width: number;
  readonly height: number;
  /** devicePixelRatio actually used, clamped for sanity on retina/hidpi. */
  readonly dpr: number;
}

/**
 * Owns the device, the canvas context, and the swap-chain configuration.
 * Resizing is explicit: call `resize()` and read `size` — the renderer decides
 * when to allocate render targets, not us.
 */
export class GPU {
  readonly device: GPUDevice;
  readonly context: GPUCanvasContext;
  readonly canvas: HTMLCanvasElement;
  readonly format: GPUTextureFormat;

  private _size: GPUSize;

  private constructor(
    canvas: HTMLCanvasElement,
    device: GPUDevice,
    context: GPUCanvasContext,
    format: GPUTextureFormat,
  ) {
    this.canvas = canvas;
    this.device = device;
    this.context = context;
    this.format = format;
    this._size = { width: 1, height: 1, dpr: 1 };
    this.resize();
  }

  static async create(canvas: HTMLCanvasElement): Promise<GPU> {
    if (!("gpu" in navigator) || !navigator.gpu) {
      throw new GPUUnsupportedError(
        "This browser has no WebGPU. Try a recent Chrome, Edge, or Safari Technology Preview.",
      );
    }

    const adapter = await navigator.gpu.requestAdapter({
      powerPreference: "high-performance",
    });
    if (!adapter) {
      throw new GPUUnsupportedError(
        "No GPU adapter answered. Hardware acceleration may be disabled.",
      );
    }

    const device = await adapter.requestDevice({ label: "aanya.space device" });
    device.lost.then((info) => {
      // We don't auto-recover yet; surface it loudly during development.
      console.error(`[gpu] device lost: ${info.reason} — ${info.message}`);
    });

    const context = canvas.getContext("webgpu");
    if (!context) {
      throw new GPUUnsupportedError("Canvas refused a 'webgpu' context.");
    }

    const format = navigator.gpu.getPreferredCanvasFormat();
    context.configure({
      device,
      format,
      alphaMode: "premultiplied",
    });

    return new GPU(canvas, device, context, format);
  }

  get size(): GPUSize {
    return this._size;
  }

  /**
   * Match the drawing buffer to the canvas' CSS box × devicePixelRatio.
   * Returns true when the dimensions actually changed (so callers can lazily
   * reallocate targets instead of every frame).
   */
  resize(): boolean {
    const dpr = Math.min(globalThis.devicePixelRatio || 1, 2);
    const rect = this.canvas.getBoundingClientRect();
    const width = Math.max(1, Math.round(rect.width * dpr));
    const height = Math.max(1, Math.round(rect.height * dpr));

    if (width === this._size.width && height === this._size.height && dpr === this._size.dpr) {
      return false;
    }

    this.canvas.width = width;
    this.canvas.height = height;
    this._size = { width, height, dpr };
    return true;
  }

  /** The texture view for this frame's swap-chain image. */
  currentView(): GPUTextureView {
    return this.context.getCurrentTexture().createView();
  }
}
